/**
 * Minimal Cognito hosted-UI client (Authorization Code + PKCE).
 *
 * The SPA is a *public* client: no secret, PKCE only. Tokens live in
 * `sessionStorage` (cleared when the tab closes) — never `localStorage` — and
 * the verified `custom:tenant_id` claim is read only for display; the API
 * re-derives and enforces tenant scoping from the JWT server-side.
 *
 * No dependency on amazon-cognito-identity-js / aws-amplify: the hosted-UI
 * OAuth2 endpoints are a few fetch + redirect calls, and avoiding the SDK
 * keeps the bundle small and the trust surface tiny.
 */

import { isSafeReturnPath, safeReturnPath } from "./returnPath";

export interface CognitoConfig {
  /** Hosted-UI domain, e.g. `https://cloudox-acme.auth.eu-west-1.amazoncognito.com`. */
  readonly domain: string;
  /** App client id (also the JWT audience). */
  readonly clientId: string;
  /** OAuth2 redirect URI registered on the app client. */
  readonly redirectUri: string;
  /** Post-logout redirect URI. */
  readonly logoutUri: string;
  /** OAuth scopes; defaults to openid/email/profile. */
  readonly scopes?: readonly string[];
}

export interface AuthTokens {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken?: string;
  /** Epoch seconds at which the id/access tokens expire. */
  readonly expiresAt: number;
}

export interface UserClaims {
  readonly sub?: string;
  readonly email?: string;
  readonly tenantId?: string;
  readonly groups: readonly string[];
}

/**
 * Why a hosted-UI redirect did not end in a session.
 *
 * Coarse on purpose. Each value maps to one thing the console can *say* to the
 * person in front of it; the underlying provider text is not surfaced because
 * it can carry identifiers and cannot be trusted as display copy.
 */
export type SignInFailure =
  /** `state` absent or not the value we issued — a replayed or forged callback. */
  | "state_mismatch"
  /** Cognito itself declined (`?error=…`), e.g. the user cancelled. */
  | "provider_error"
  /** The code-for-token exchange was rejected. */
  | "exchange_failed";

/** Outcome of handling a hosted-UI redirect. */
export type CallbackOutcome =
  | { readonly status: "signed-in"; readonly claims: UserClaims }
  /** The URL carried neither a code nor an error — not a callback at all. */
  | { readonly status: "not-a-callback" }
  | { readonly status: "failed"; readonly reason: SignInFailure };

const STORAGE_KEY = "cloudox.auth.tokens";
const PKCE_VERIFIER_KEY = "cloudox.auth.pkce_verifier";
const STATE_KEY = "cloudox.auth.state";
const RETURN_PATH_KEY = "cloudox.auth.return_path";
const SIGNED_OUT_KEY = "cloudox.auth.signed_out";
const DEFAULT_SCOPES = ["openid", "email", "profile"] as const;

/** Build the hosted-UI authorize URL (pure; no side effects). */
export function buildAuthorizeUrl(
  config: CognitoConfig,
  params: { state: string; codeChallenge: string },
): string {
  const scopes = (config.scopes ?? DEFAULT_SCOPES).join(" ");
  const query = new URLSearchParams({
    response_type: "code",
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: scopes,
    state: params.state,
    code_challenge: params.codeChallenge,
    code_challenge_method: "S256",
  });
  return `${trimEnd(config.domain)}/oauth2/authorize?${query.toString()}`;
}

/** Build the hosted-UI logout URL (pure). */
export function buildLogoutUrl(config: CognitoConfig): string {
  const query = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  });
  return `${trimEnd(config.domain)}/logout?${query.toString()}`;
}

/** Decode (without verifying) a JWT's payload into typed user claims. */
export function decodeClaims(idToken: string): UserClaims {
  const payload = decodeJwtPayload(idToken);
  const rawGroups = payload["cognito:groups"];
  const groups = Array.isArray(rawGroups) ? rawGroups.map(String) : [];
  return {
    sub: typeof payload.sub === "string" ? payload.sub : undefined,
    email: typeof payload.email === "string" ? payload.email : undefined,
    tenantId:
      typeof payload["custom:tenant_id"] === "string"
        ? payload["custom:tenant_id"]
        : undefined,
    groups,
  };
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split(".");
  const payload = parts[1];
  if (!payload) throw new Error("malformed JWT");
  const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const binary =
    typeof atob === "function"
      ? atob(padded)
      : Buffer.from(padded, "base64").toString("binary");
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes));
  return parsed as Record<string, unknown>;
}

async function createPkcePair(): Promise<{
  verifier: string;
  challenge: string;
}> {
  const verifier = randomUrlSafe(64);
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  const challenge = base64UrlEncode(new Uint8Array(digest));
  return { verifier, challenge };
}

function randomUrlSafe(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  const b64 =
    typeof btoa === "function"
      ? btoa(binary)
      : Buffer.from(binary, "binary").toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function trimEnd(s: string): string {
  return s.replace(/\/+$/, "");
}

/** Stateful auth client bound to the browser (storage + location). */
export class CognitoAuthClient {
  constructor(
    private readonly config: CognitoConfig,
    private readonly storage: Storage = sessionStorage,
  ) {}

  /** True when a non-expired id token is present. */
  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return tokens !== undefined && tokens.expiresAt * 1000 > Date.now();
  }

  /**
   * True when this tab held a session that has since aged out.
   *
   * Lets the sign-in page say *"your session expired"* instead of the identical
   * blank prompt a first-time visitor sees — the difference between explaining
   * an interruption and appearing to have forgotten the user.
   */
  hasExpiredSession(): boolean {
    return this.getTokens() !== undefined && !this.isAuthenticated();
  }

  getIdToken(): string | undefined {
    return this.isAuthenticated() ? this.getTokens()?.idToken : undefined;
  }

  getClaims(): UserClaims | undefined {
    const idToken = this.getIdToken();
    return idToken ? decodeClaims(idToken) : undefined;
  }

  getTokens(): AuthTokens | undefined {
    const raw = this.storage.getItem(STORAGE_KEY);
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as AuthTokens;
    } catch {
      return undefined;
    }
  }

  /**
   * Begin sign-in: persist PKCE + state (+ where to land afterwards), then
   * redirect to the hosted UI.
   *
   * `returnTo` is stored only if it passes {@link isSafeReturnPath}; an
   * off-origin or malformed value is dropped so the callback falls back to the
   * console root instead of carrying an attacker's destination through the
   * round trip. It is never put in the authorize URL — the `state` parameter
   * stays an opaque nonce whose only job is matching the callback.
   */
  async login(returnTo?: string): Promise<void> {
    const { verifier, challenge } = await createPkcePair();
    const state = randomUrlSafe(16);
    this.storage.setItem(PKCE_VERIFIER_KEY, verifier);
    this.storage.setItem(STATE_KEY, state);
    if (isSafeReturnPath(returnTo)) {
      this.storage.setItem(RETURN_PATH_KEY, returnTo as string);
    } else {
      this.storage.removeItem(RETURN_PATH_KEY);
    }
    window.location.assign(
      buildAuthorizeUrl(this.config, { state, codeChallenge: challenge }),
    );
  }

  /**
   * Read and clear the stored post-sign-in destination.
   *
   * Re-validated on read as well as on write: storage is same-origin but not
   * beyond reach (another tab, an XSS-planted value), and the cost of checking
   * twice is nil.
   */
  consumeReturnPath(): string {
    const stored = this.storage.getItem(RETURN_PATH_KEY);
    this.storage.removeItem(RETURN_PATH_KEY);
    return safeReturnPath(stored);
  }

  /**
   * Complete sign-in on the callback route: validate `state`, exchange the
   * authorization `code` for tokens, and persist them.
   *
   * Fails closed. Every path that does not end in a session clears the PKCE
   * verifier and the expected `state` first, so a rejected or abandoned attempt
   * cannot be retried against leftover single-use material — a second callback
   * bearing the same `state` finds nothing to match and is refused.
   *
   * Never throws: the caller renders the returned {@link CallbackOutcome}. The
   * provider's own `error_description` is deliberately not propagated (it can
   * embed identifiers and is not display copy).
   */
  async handleRedirectCallback(
    search: string = window.location.search,
  ): Promise<CallbackOutcome> {
    const params = new URLSearchParams(search);
    // Cognito reports a declined authorization (cancelled, consent refused,
    // misconfigured client) as `?error=` with no code.
    if (params.get("error")) {
      this.clearPending();
      return { status: "failed", reason: "provider_error" };
    }
    const code = params.get("code");
    if (!code) return { status: "not-a-callback" };

    const expectedState = this.storage.getItem(STATE_KEY);
    const verifier = this.storage.getItem(PKCE_VERIFIER_KEY);
    // Consume both before doing anything with the code: they are single-use.
    this.clearPending();
    if (!expectedState || params.get("state") !== expectedState) {
      return { status: "failed", reason: "state_mismatch" };
    }
    if (!verifier) return { status: "failed", reason: "state_mismatch" };

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: this.config.clientId,
      code,
      redirect_uri: this.config.redirectUri,
      code_verifier: verifier,
    });
    let json: {
      id_token: string;
      access_token: string;
      refresh_token?: string;
      expires_in: number;
    };
    try {
      const response = await fetch(
        `${trimEnd(this.config.domain)}/oauth2/token`,
        {
          method: "POST",
          headers: { "content-type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        },
      );
      if (!response.ok) return { status: "failed", reason: "exchange_failed" };
      json = (await response.json()) as typeof json;
      if (!json.id_token || !json.access_token) {
        return { status: "failed", reason: "exchange_failed" };
      }
    } catch {
      // Network failure, CORS rejection, unparseable body — indistinguishable
      // from the caller's point of view, and none of them yields a session.
      return { status: "failed", reason: "exchange_failed" };
    }

    const tokens: AuthTokens = {
      idToken: json.id_token,
      accessToken: json.access_token,
      refreshToken: json.refresh_token,
      expiresAt: Math.floor(Date.now() / 1000) + (json.expires_in ?? 3600),
    };
    this.storage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    return { status: "signed-in", claims: decodeClaims(tokens.idToken) };
  }

  /** Discard single-use PKCE material so a callback cannot be replayed. */
  private clearPending(): void {
    this.storage.removeItem(PKCE_VERIFIER_KEY);
    this.storage.removeItem(STATE_KEY);
  }

  /**
   * True exactly once after a deliberate sign-out, so the sign-in page can
   * confirm the action rather than looking like a session that dropped.
   *
   * The flag rides in session storage because the sign-out is a full-page
   * redirect through the hosted UI — React state does not survive it, and the
   * registered `logout_uri` is fixed infrastructure we do not want to encode UI
   * state into.
   */
  consumeSignedOutFlag(): boolean {
    const flagged = this.storage.getItem(SIGNED_OUT_KEY) === "1";
    if (flagged) this.storage.removeItem(SIGNED_OUT_KEY);
    return flagged;
  }

  /** Discard every trace of the local session, then leave for the hosted-UI
   * sign-out. Local state is cleared *before* the redirect so an interrupted
   * navigation still leaves the tab signed out. */
  logout(): void {
    this.storage.removeItem(STORAGE_KEY);
    this.storage.removeItem(RETURN_PATH_KEY);
    this.clearPending();
    // Set after clearing, so it is the one thing that survives on purpose.
    this.storage.setItem(SIGNED_OUT_KEY, "1");
    window.location.assign(buildLogoutUrl(this.config));
  }
}
